import { ContestType } from '@betterpool/api-types/contests-service';
import { useTheme } from '@emotion/react';
import { createStyles, Box, Stack, Pagination, Center, Skeleton } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/testing';
import { Text } from '~/domains/design-system/Text';
import Modal from '~/domains/common/components/Modal/Modal/Modal';
import DrawerTitle from '~/domains/design-system/components/Drawer/DrawerTitle';
import useContestSlates from '~/domains/games/hooks/useContestSlates';
import repeat from '~/common/utils/repeat';
import { pluralizeSlateLabel } from '~/domains/contest/domains/managev2/domains/common/utils/pluralizeSlateLabel';

import GameList from './GameList';
import { useFormattedSlateDate } from './useFormattedSlateDate';

const useStyles = createStyles((theme) => ({
  slateBox: {
    borderColor: theme.colors.gray[2],
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: theme.spacing.md / 2,
  },
  paginationContainer: {
    marginTop: theme.spacing.lg,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  drawer: {
    borderTopLeftRadius: theme.other.spacing._16,
    borderTopRightRadius: theme.other.spacing._16,
    height: '100%',
    overflow: 'scroll',
  },
  drawerBody: {
    borderTopLeftRadius: theme.other.spacing._16,
    borderTopRightRadius: theme.other.spacing._16,
    paddingTop: theme.other.spacing._16,
    maxHeight: '100svh',
    overflow: 'auto',
  },
}));

const PAGE_SIZE = 20;

type GameModalProps = {
  scheduleId?: string;
  contestType: ContestType;
  finalSlateId?: string;
  isOpen: boolean;
  onClose: VoidFunction;
  slatesInfo?: string;
  slateLabel: string;
  groupStageSlateId?: string;
};

function GameModal({
  scheduleId,
  contestType,
  finalSlateId,
  isOpen,
  slateLabel,
  onClose,
  slatesInfo,
  groupStageSlateId,
}: GameModalProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('contest');
  const { formatSlateDate } = useFormattedSlateDate();

  const slates = useContestSlates({
    scheduleId,
    contestType,
    finalSlateId,
    pageSize: PAGE_SIZE,
    enabled: isOpen,
  });

  const { currentPage, data, pagesTotal, onCurrentPageChange, isLoading } = slates;
  const slatesData = data?.data || [];
  const getSlateLabel = (name: string, index: number): string => {
    if (contestType === ContestType.PLAYER_SURVIVOR && slateLabel === 'Day') {
      return `${slateLabel} ${index + 1}`;
    }
    return name;
  };

  return (
    <Modal
      classNames={{ drawer: { body: classes.drawerBody, drawer: classes.drawer } }}
      onClose={onClose}
      isOpened={isOpen}
      size="lg"
    >
      <DrawerTitle isAddTopPaddingOnMobile>
        {t('detail.about.game.modal.title', {
          slateLabel: pluralizeSlateLabel(2, slateLabel),
        })}
      </DrawerTitle>

      <Stack
        data-test-id={dt.contestDetail.section.about.game.slate.container}
        spacing={theme.spacing.xs}
        /* eslint-disable-line local-rules/use-theme-tokens */ mt={20}
      >
        {isLoading && (
          <Box className={classes.loadingContainer}>
            {repeat(5, (index) => (
              <Skeleton h={100} key={index} />
            ))}
          </Box>
        )}
        {!isLoading && slatesData.length === 0 && (
          <Box className={classes.loadingContainer}>
            <Text variant="body-medium">{t('detail.about.game.modal.nogames')}</Text>
          </Box>
        )}
        {!isLoading &&
          slatesData.map(({ games, id, startDate, name }, index) => (
            <Box pos="relative" key={`${id + index}`} className={classes.slateBox}>
              <GameList
                games={games}
                slateDate={startDate ? formatSlateDate(startDate) : undefined}
                disableExpand={id === groupStageSlateId}
                slateLabel={getSlateLabel(name, index)}
              />
            </Box>
          ))}
        {slatesInfo ? (
          <Text align="center" c="gray2.5" mt={theme.other.spacing._4} variant="body-medium">
            {slatesInfo}
          </Text>
        ) : null}
      </Stack>
      {pagesTotal > 1 && onCurrentPageChange && (
        <Center className={classes.paginationContainer}>
          <Pagination
            total={pagesTotal}
            page={currentPage}
            onChange={onCurrentPageChange}
            size="sm"
          />
        </Center>
      )}
    </Modal>
  );
}

export default GameModal;
